import { createElement, type ReactElement } from "react";
import { Glyph, type GlyphMeaning, type GlyphSize } from "@apf/design-system/glyphs";

export interface IconProps {
  className?: string;
  size?: GlyphSize;
}

export type IconComponent = (props: IconProps) => ReactElement | null;

/**
 * Every glyph is hidden from assistive technology: the accessible name belongs to the
 * control or the visible text beside it, not to the mark. An icon-only control names
 * itself with aria-label on the control, as the drawer close button does.
 */

/**
 * Resolves the icon tokens used by framework content (framework/content/core.json)
 * to governed glyph meanings in the design-system semantic ledger
 * (packages/design-system/glyphs/semantic.json).
 *
 * A token with no registered meaning renders nothing: the visible text label beside
 * the icon carries the meaning on its own. No substitute symbol is drawn. The tokens
 * that resolve to nothing today are recorded as gaps in that ledger, so the silence is
 * a decision rather than an oversight: `target` (battlecards) is the only one in the
 * shipped configuration.
 */
const meaningForToken: Record<string, GlyphMeaning> = {
  compass: "domain.strategy",
  inbox: "domain.intake",
  gavel: "domain.sourcing",
  "file-signature": "domain.clm",
  "shield-check": "domain.tprm",
  "shopping-cart": "domain.buying",
  receipt: "domain.invoice",
  handshake: "domain.performance",
  "trending-up": "domain.value",
  radar: "capability.control-tower",
  sparkles: "capability.agent-tools",
  database: "capability.data",
  plug: "capability.integration",
  files: "capability.composer",
  composer: "capability.composer",
  conductor: "capability.conductor",
  fingerprint: "capability.controls",
  blocks: "object.workblock",
  "messages-square": "capability.messaging",
  grid: "utility.grid",
  box: "object.workblock",
  network: "foundation.execution-rails",
  map: "utility.map",
  "book-open": "utility.glossary",
  "circle-help": "utility.help",
  "git-branch": "utility.release",
  search: "utility.search",
  theme: "utility.theme",
  filter: "utility.filter",
  "external-link": "utility.external-link",
  "arrow-right": "utility.arrow-right",
  check: "utility.check",
  x: "utility.close"
};

function labelOnly(): null {
  return null;
}

const componentCache = new Map<string, IconComponent>();

export function iconFor(name: string): IconComponent {
  const cached = componentCache.get(name);
  if (cached) return cached;
  const meaning = meaningForToken[name];
  const component: IconComponent = meaning
    ? ({ className, size }) =>
        createElement(Glyph, {
          meaning,
          className,
          size: size ?? (meaning.startsWith("utility.") ? 20 : 24)
        })
    : labelOnly;
  componentCache.set(name, component);
  return component;
}
